import { httpClient } from './api/httpClient';
import { isMockEnabled, mockResponse } from './api/mockAdapter';
import { API_ENDPOINTS } from 'constants/apiEndpoints';
import { STAGE1_STATUS, STAGE2_STATUS, BRIEFING_STATUS, ENTITY_STATUS, CRON_RUN_STATUS } from 'constants/status';
import {
  MOCK_STAGE1,
  MOCK_STAGE2,
  MOCK_BRIEFING,
  MOCK_STORAGE1,
  MOCK_STORAGE2,
} from 'mock/pipeline.mock';
import { MOCK_CRON_JOBS } from 'mock/cronJobs.mock';

const toSummary = (raw) => {
  if (!raw) return null;
  return {
    historyDays: raw.history_days ?? raw.historyDays ?? 40,
    stats: {
      totalMined: raw.stats?.total_mined ?? raw.stats?.totalMined ?? 0,
      stage1Pending: raw.stats?.stage1_pending ?? raw.stats?.stage1Pending ?? 0,
      stage2Pending: raw.stats?.stage2_queue ?? raw.stats?.stage2_pending ?? raw.stats?.stage2Pending ?? 0,
      briefingQueue: raw.stats?.briefing_pending ?? raw.stats?.briefing_queue ?? raw.stats?.briefingQueue ?? 0,
      storage1Count: raw.stats?.storage1_count ?? raw.stats?.storage1Count ?? 0,
      storage2Count: raw.stats?.storage2_count ?? raw.stats?.storage2Count ?? 0,
      totalApproved: raw.stats?.total_approved ?? raw.stats?.totalApproved ?? 0,
      totalRejected: raw.stats?.total_rejected ?? raw.stats?.totalRejected ?? 0,
      manualUpload: raw.stats?.manual_upload ?? raw.stats?.manualUpload ?? 0,
      cronProcessed: raw.stats?.cron_processed ?? raw.stats?.cronProcessed ?? 0,
    },
    workflow: raw.workflow ?? {},
    rates: {
      approvalRate: raw.rates?.approval_rate ?? raw.rates?.approvalRate ?? 0,
      rejectionRate: raw.rates?.rejection_rate ?? raw.rates?.rejectionRate ?? 0,
      manualPct: raw.rates?.manual_pct ?? raw.rates?.manualPct ?? 0,
      cronPct: raw.rates?.cron_pct ?? raw.rates?.cronPct ?? 0,
    },
    today: {
      articles: raw.today?.articles ?? 0,
      approved: raw.today?.approved ?? 0,
      routed: raw.today?.routed ?? 0,
      dispatched: raw.today?.dispatched ?? 0,
    },
    cronHealth: {
      active: raw.cron_health?.active ?? raw.cronHealth?.active ?? 0,
      successful: raw.cron_health?.successful ?? raw.cronHealth?.successful ?? 0,
      failed: raw.cron_health?.failed ?? raw.cronHealth?.failed ?? 0,
      jobs: (raw.cron_health?.jobs ?? raw.cronHealth?.jobs ?? []),
    },
  };
};

const buildMockSummary = () => {
  const stage1Pending = MOCK_STAGE1.filter((a) => a.status === STAGE1_STATUS.PENDING).length;
  const stage2Pending = MOCK_STAGE2.filter((a) => a.status === STAGE2_STATUS.PENDING).length;
  const briefingQueue = MOCK_BRIEFING.filter((a) => a.status === BRIEFING_STATUS.PENDING).length;
  return toSummary({
    history_days: 40,
    stats: {
      total_mined: MOCK_STAGE1.length,
      stage1_pending: stage1Pending,
      stage2_pending: stage2Pending,
      briefing_queue: briefingQueue,
      storage1_count: MOCK_STORAGE1.length,
      storage2_count: MOCK_STORAGE2.length,
      total_approved: 0,
      total_rejected: 0,
      manual_upload: 0,
      cron_processed: MOCK_STAGE1.length,
    },
    workflow: {
      cron: MOCK_CRON_JOBS.filter((c) => c.status === ENTITY_STATUS.ACTIVE).length,
      stage1: MOCK_STAGE1.length,
      stage2: MOCK_STAGE2.length,
      briefing: MOCK_BRIEFING.length,
      storage1: MOCK_STORAGE1.length,
      storage2: MOCK_STORAGE2.length,
    },
    rates: { approval_rate: 0, rejection_rate: 0, manual_pct: 0, cron_pct: 100 },
    today: { articles: 0, approved: 0, routed: 0, dispatched: 0 },
    cron_health: {
      active: MOCK_CRON_JOBS.filter((c) => c.status === ENTITY_STATUS.ACTIVE).length,
      successful: MOCK_CRON_JOBS.filter((c) => c.last_run_status === CRON_RUN_STATUS.SUCCESS).length,
      failed: MOCK_CRON_JOBS.filter((c) => c.last_run_status === CRON_RUN_STATUS.FAILED).length,
      jobs: MOCK_CRON_JOBS.map((c) => ({
        id: c.id,
        jobName: c.job_name,
        newspaperName: c.newspaper_name,
        edition: c.edition,
        status: c.status,
        lastRunStatus: c.last_run_status,
        lastRunAt: c.last_run_at,
      })),
    },
  });
};

const toCharts = (raw) => raw && {
  dailyTrend: raw.daily_trend ?? raw.dailyTrend ?? [],
  stageDistribution: raw.stage_distribution ?? raw.stageDistribution ?? {},
  sourceDistribution: raw.source_distribution ?? raw.sourceDistribution ?? {},
  topNewspapers: raw.top_newspapers ?? raw.topNewspapers ?? [],
  approvalTrend: raw.approval_trend ?? raw.approvalTrend ?? [],
};

const toActivity = (raw) => ({
  id: raw.id,
  module: raw.module,
  operation: raw.operation,
  description: raw.description,
  performedByName: raw.performed_by_name ?? raw.performedByName ?? '',
  createdAt: raw.created_at ?? raw.createdAt ?? null,
});

const toPendingActions = (raw) => raw && {
  stage1Pending: raw.stage1_pending ?? raw.stage1Pending ?? 0,
  stage2Pending: raw.stage2_pending ?? raw.stage2Pending ?? 0,
  briefingUndecided: raw.briefing_undecided ?? raw.briefingUndecided ?? 0,
  briefingReadyDispatch: raw.briefing_ready_dispatch ?? raw.briefingReadyDispatch ?? 0,
};

export const dashboardService = {
  async getSummary() {
    if (isMockEnabled()) return mockResponse(buildMockSummary());
    const raw = await httpClient.get(API_ENDPOINTS.DASHBOARD.SUMMARY);
    return toSummary(raw);
  },

  async getCharts() {
    if (isMockEnabled()) return mockResponse(toCharts({ daily_trend: [], stage_distribution: {}, source_distribution: {}, top_newspapers: [], approval_trend: [] }));
    const raw = await httpClient.get(API_ENDPOINTS.DASHBOARD.CHARTS);
    return toCharts(raw);
  },

  async getActivities(limit = 10) {
    if (isMockEnabled()) return [];
    const data = await httpClient.get(API_ENDPOINTS.DASHBOARD.ACTIVITIES, { params: { limit } });
    return (Array.isArray(data) ? data : data.items ?? []).map(toActivity);
  },

  async getPendingActions() {
    if (isMockEnabled()) return toPendingActions({ stage1_pending: 0, stage2_pending: 0, briefing_undecided: 0, briefing_ready_dispatch: 0 });
    const raw = await httpClient.get(API_ENDPOINTS.DASHBOARD.PENDING_ACTIONS);
    return toPendingActions(raw);
  },
};

export default dashboardService;
